import { I18nContext } from 'next-i18next';
import { createContext, createElement, Fragment, type ReactElement, type ReactNode, useContext, useMemo } from 'react';

/**
 * Glossário de marcas para a caixa alta em turco (IDIO20).
 *
 * `text-transform: uppercase` segue o `lang` do elemento: em turco (e azerbaijano) o "i" vira "İ", correto para o
 * texto turco, mas que deforma marcas e nomes estrangeiros ("NOPİNG", "WİNDOWS", "FORTNİTE"). O helper envolve SÓ o
 * nome da marca num `<np-brand lang="en" translate="no" style="display:contents">` (no SVG, `<tspan>`), sem o sufixo
 * turco: "NoPing’i" → `<np-brand lang="en">NoPing</np-brand>’i` → "NOPING’İ". Fora de tr/az devolve o texto como veio (DOM
 * idêntico).
 *
 * Por que `np-brand` com `display: contents` e não um `<span>`: muitos desses textos estão em contêineres flex com `gap`
 * (eyebrows, CTAs, selos); um `<span>` viraria um item flex à parte (quebra de linha e o `gap` no meio da frase), e
 * seletores como `.npt-eyebrow span` (o ponto decorativo) o estilizariam. Com `display: contents` o texto continua no
 * mesmo item flex anônimo, sem caixa própria (mesmo desenho), e a caixa alta ainda segue o `lang` do elemento.
 *
 * Só entram nomes estrangeiros (marcas, produtos, jogos, plataformas, provedores, pessoas) com "i" minúsculo, o único
 * caractere que a caixa turca muda no alfabeto latino; um nome sem "i" fica igual nas duas caixas e não precisa de
 * marcação. Ficam de fora: marcas turcas (Superonline, Türk Telekom), nomes de lugar (têm forma turca) e palavras
 * comuns emprestadas do inglês ("online", "spike", "uptime"), que seguem a ortografia turca.
 */
export const BRAND_GLOSSARY: readonly string[] = [
  // NoPing e produtos (nomes fixos)
  'NoPing Mobile',
  'NoPing',
  'Multi Connection',
  'Multi Internet',
  'Multi internet',
  'Multi',
  'AudioPad',
  'Audiopad',
  'Aim Trainer',
  'Pro Settings',
  // plataformas, sistemas e serviços
  'Windows',
  'Microsoft',
  'Android',
  'iOS',
  'iPhone',
  'iPad',
  'Wi-Fi',
  'DigiCert',
  'Twitch',
  'TikTok',
  'Discord',
  'LinkedIn',
  'Instagram',
  'Kick',
  'Epic Games',
  'Riot Games',
  // jogos e modos
  'Counter-Strike',
  'Premier',
  'Fortnite',
  'Minecraft',
  'Hypixel',
  'Tibia',
  'Metin2',
  'Knight Online',
  'Albion Online',
  'Silkroad Online',
  'Ragnarok Online',
  'Point Blank',
  'Final Fantasy XIV',
  'Final Fantasy',
  'Battlefield',
  'Rainbow Six Siege',
  'Rainbow Six',
  'Genshin Impact',
  'Free Fire',
  'Wild Rift',
  'Mobile Legends',
  'PUBG Mobile',
  'COD Mobile',
  'Call of Duty: Mobile',
  'FC Online',
  'FIFA Online',
  'Marvel Rivals',
  'Dead by Daylight',
  'Arc Raiders',
  'ARC Raiders',
  'Destiny 2',
  'Path of Exile',
  'Teamfight Tactics',
  'Arena Breakout: Infinite',
  'The Finals',
  'FiveM',
  // concorrentes e provedores estrangeiros
  'ExitLag',
  'Mudfish',
  'Ziggo',
  'Odido',
  'CableLink',
  'Delta Fiber',
];

/** Elemento da marca no HTML: sem caixa própria (`display: contents`), só o idioma da caixa alta e o `translate=no`. */
export const BRAND_TAG = 'np-brand';
const BRAND_PROPS = { lang: 'en', translate: 'no', style: { display: 'contents' } } as const;

/** Idiomas cuja caixa alta troca o "i" latino por "İ" (BCP 47 ou o código do i18next de um par: "tr-TR-x-tr-…"). */
export const isDottedILang = (lang: string | null | undefined): boolean => /^(tr|az)(?:-|$)/i.test(lang ?? '');

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Só os termos com "i" minúsculo (os outros não mudam na caixa turca), do mais longo ao mais curto ("NoPing Mobile"
// antes de "NoPing"). Limite = não ter letra nem algarismo colado dos dois lados; sem lookbehind (Safari < 16.4).
const TERMS = BRAND_GLOSSARY.filter((term) => term.includes('i')).sort((first, second) => second.length - first.length);
const BRAND_PATTERN = new RegExp(`(^|[^\\p{L}\\p{N}])(${TERMS.map(escapeRegExp).join('|')})(?=[^\\p{L}\\p{N}]|$)`, 'gu');

/** Pedaços do texto: string comum ou nome do glossário. Exportado para os testes. */
export function splitBrands(text: string): Array<{ brand: boolean; value: string }> {
  const parts: Array<{ brand: boolean; value: string }> = [];
  let last = 0;
  for (const match of text.matchAll(BRAND_PATTERN)) {
    const lead = match[1] ?? '';
    const brand = match[2] ?? '';
    const start = (match.index ?? 0) + lead.length;
    if (start > last) parts.push({ brand: false, value: text.slice(last, start) });
    parts.push({ brand: true, value: brand });
    last = start + brand.length;
  }
  if (last < text.length) parts.push({ brand: false, value: text.slice(last) });
  return parts;
}

export interface MarkBrandsOptions {
  /** Texto dentro de `<text>` do SVG: usa `<tspan>` (um `<span>` não é desenhado no SVG). */
  svg?: boolean;
}

/**
 * Envolve os nomes do glossário em `<np-brand lang="en" translate="no">` (`display: contents`) quando `lang` é
 * turco/azerbaijano. Em qualquer outro idioma, ou sem nome do glossário no texto, devolve o próprio texto (mesmo nó).
 */
export function markBrands(text: string | null | undefined, lang: string | null | undefined, options: MarkBrandsOptions = {}): ReactNode {
  if (typeof text !== 'string' || !isDottedILang(lang)) return text;
  const parts = splitBrands(text);
  if (!parts.some((part) => part.brand)) return text;
  const brandElement = (value: string) => (options.svg ? createElement('tspan', { lang: 'en', translate: 'no' }, value) : createElement(BRAND_TAG, BRAND_PROPS, value));
  // Filhos como argumentos do createElement: sem `key` (lista estática, igual no servidor e no cliente).
  return createElement(Fragment, null, ...parts.map((part) => (part.brand ? brandElement(part.value) : part.value)));
}

/** Tag que o `<Trans>` troca pelo elemento da marca (ver `BRAND_TRANS_COMPONENTS`). */
export const BRAND_TRANS_TAG = 'npbrand';

/**
 * Para strings do `<Trans>` (com tags como `<nw>…</nw>`): envolve os nomes do glossário em `<npbrand>…</npbrand>` só
 * nos trechos de texto, nunca dentro de uma tag. Sem nome do glossário, devolve a mesma string.
 */
export function markBrandTags(text: string): string {
  if (typeof text !== 'string') return text;
  return text
    .split(/(<[^>]*>)/)
    .map((chunk) =>
      chunk.startsWith('<')
        ? chunk
        : splitBrands(chunk)
            .map((part) => (part.brand ? `<${BRAND_TRANS_TAG}>${part.value}</${BRAND_TRANS_TAG}>` : part.value))
            .join('')
    )
    .join('');
}

/** Componentes do `<Trans>` para as tags de `markBrandTags`. */
export const BRAND_TRANS_COMPONENTS: Readonly<Record<string, ReactElement>> = { [BRAND_TRANS_TAG]: createElement(BRAND_TAG, BRAND_PROPS) };

type AnyFn = (...args: unknown[]) => unknown;

export interface BrandMarker {
  (text: string | null | undefined, options?: MarkBrandsOptions): ReactNode;
  /** true quando o idioma da página é tr/az (a marcação está ligada). */
  readonly active: boolean;
  /** Para o `t` de um `<Trans>`: em tr/az, devolve um `t` que marca as marcas com `<npbrand>`; senão, o próprio `t`. */
  readonly transT: <T>(t: T) => T;
  /** Componentes a somar aos do `<Trans>` (vazio fora de tr/az). */
  readonly transComponents: Readonly<Record<string, ReactElement>>;
}

const NO_COMPONENTS: Readonly<Record<string, ReactElement>> = {};

/**
 * `markBrands` com o idioma do i18next da página (o mesmo no SSR e na hidratação). Lê o contexto do provedor do
 * `appWithTranslation` em vez de chamar `useTranslation()` sem namespace, que nos pares sem namespace padrão tentaria
 * carregar "translation". O `I18nContext` vem de next-i18next de propósito: no servidor o next-i18next carrega o
 * react-i18next em CommonJS, e um import direto de 'react-i18next' pode cair na outra cópia (ESM), com outro contexto —
 * no SSR o hook ficaria desligado e só o cliente marcaria (erro de hidratação).
 */
// Testes que trocam o next-i18next por um mock sem `I18nContext`: contexto vazio (marcação desligada), nunca um erro.
const NO_I18N_CONTEXT = createContext<{ i18n?: { language?: string } } | null>(null);

export function useBrandMarker(): BrandMarker {
  const lang = useContext(I18nContext ?? NO_I18N_CONTEXT)?.i18n?.language;
  return useMemo(() => {
    const active = isDottedILang(lang);
    const mark = ((text: string | null | undefined, options?: MarkBrandsOptions) => (active ? markBrands(text, lang, options) : text)) as BrandMarker;
    // O <Trans> lê o namespace e o idioma do próprio `t` (t.ns, t.lng, t.keyPrefix do getFixedT): o `t` marcado leva
    // essas propriedades junto, senão o Trans procuraria a chave no namespace padrão (que os pares não têm).
    const transT = <T,>(t: T): T =>
      active
        ? (Object.assign((...args: unknown[]) => {
            const value = (t as unknown as AnyFn)(...args);
            return typeof value === 'string' ? markBrandTags(value) : value;
          }, t) as unknown as T)
        : t;
    Object.defineProperties(mark, { active: { value: active }, transT: { value: transT }, transComponents: { value: active ? BRAND_TRANS_COMPONENTS : NO_COMPONENTS } });
    return mark;
  }, [lang]);
}
